import { HTTPClientInterface, HTTPRequest } from '@shared/core/HTTPClientInterface';
import { buildURL, SystemURL } from "./URLCatalog";

interface DbNovedadesAuditChange {
    field: string;
    oldValue?: unknown;
    newValue?: unknown;
    previousValue?: unknown;
    valueBefore?: unknown;
    valueAfter?: unknown;
}

interface DbNovedadesAuditLog {
    timestamp: number;
    organizationID: string;
    action: string;
    section: string;
    entityType: string;
    entityID?: string;
    changingUser: {
        id: string;
        name: string;
        image?: string;
    };
    firestoreRecordId: string;
    changes?: DbNovedadesAuditChange[];
}

export interface NovedadesAuditChange {
    field: string;
    oldValue: unknown;
    newValue: unknown;
}

export interface NovedadesAuditLog {
    id: string;
    action: string;
    fecha: string;
    hora: string;
    tipo: string;
    responsable: string;
    section: string;
    entityType: string;
    entityID?: string;
    changes: NovedadesAuditChange[];
}

export interface Novedad {
    name: string;
    acronym: string;
    payrollCode?: string;
    backgroundColor?: string;
    foregroundColor?: string;
    disabled?: boolean;
    isUsableInLicenses?: boolean;
}

// Request interfaces
export interface ConfirmNovedadRequest {
    novedad: string;
    novedadAcronym: string;
}

export interface CreateNovedadRequest {
    novedad: string;
    novedadAcronym: string;
    payrollCode?: string;
    backgroundColor?: string;
    foregroundColor?: string;
}

export interface DisableNovedadRequest {
    novedadAcronym: string;
}

export interface LicenseEnabledNovedadRequest {
    novedadAcronym: string;
    isUsableInLicenses: boolean;
}

export interface UserStatusUpdateRequest {
    novedad: string;
    novedadAcronym: string;
    userID: string;
}

export interface BulkUserStatusUpdateRequest {
    novedad: string;
    novedadAcronym: string;
    userID: string;
    dates: string[];
}

// Configuration interfaces
export interface WorkingDaysRequest {
    days: string[];
}

export interface HolidaysRequest {
    dates: string[];
}

function formatNovedadesAuditAction(action: string): string {
    switch (action) {
        case "WORKING_DAYS_UPDATED":
            return "Días laborables actualizados";
        case "HOLIDAYS_UPDATED":
            return "Feriados actualizados";
        case "NOVEDAD_CREATED":
            return "Novedad creada";
        case "NOVEDAD_UPDATED":
            return "Novedad actualizada";
        case "NOVEDAD_DISABLED":
            return "Novedad deshabilitada";
        default:
            return action;
    }
}

function convertDbAuditChangeToNovedadesAuditChange(
    dbAuditChange: DbNovedadesAuditChange,
): NovedadesAuditChange {
    return {
        field: dbAuditChange.field,
        oldValue: dbAuditChange.oldValue ?? dbAuditChange.previousValue ?? dbAuditChange.valueBefore,
        newValue: dbAuditChange.newValue ?? dbAuditChange.valueAfter,
    };
}

function convertDbAuditLogToNovedadesAuditLog(
    dbAuditLog: DbNovedadesAuditLog,
): NovedadesAuditLog {
    const date = new Date(dbAuditLog.timestamp);
    const fecha = date.toLocaleDateString("es-ES");
    const hora = date.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });

    return {
        id: dbAuditLog.firestoreRecordId,
        action: dbAuditLog.action,
        fecha,
        hora,
        tipo: formatNovedadesAuditAction(dbAuditLog.action),
        responsable: dbAuditLog.changingUser?.name || "Sistema",
        section: dbAuditLog.section,
        entityType: dbAuditLog.entityType,
        entityID: dbAuditLog.entityID,
        changes: (dbAuditLog.changes ?? []).map(convertDbAuditChangeToNovedadesAuditChange),
    };
}

export class NovedadesService {
    private httpClient: HTTPClientInterface;

    constructor(httpClient: HTTPClientInterface) {
        this.httpClient = httpClient;
    }

    private addOrganizationHeader(request: HTTPRequest, orgId: string) {
        request.headers = {
            ...(request.headers ?? {}),
            "x-organization-id": orgId,
        };
    }


    async closeRequestForInformation(id: string): Promise<any> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Novedades.postCloseRfi, { id });
        request.method = "POST";
        return this.httpClient.execute(request);
    }

    async getInasistenciasPaginated(orgId: string, lastKey: string = '', pageSize: number = 50): Promise<any> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Novedades.getInasistenciasPaginated,
            { org_id: orgId, lastKey: lastKey, pageSize: pageSize.toString() });
        request.method = "GET";
        this.addOrganizationHeader(request, orgId);
        const response = await this.httpClient.execute(request);
        return response.result;
    }

    async changeNovedadForInasistencia(orgId: string, rowId: string, confirmData: ConfirmNovedadRequest): Promise<any> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Novedades.updateNovedadData, { org_id: orgId, row_id: rowId });
        request.method = "POST";
        request.body = confirmData;
        this.addOrganizationHeader(request, orgId);
        return this.httpClient.execute(request);
    }

    async changeUserStatusForDay(orgId: string, date: string, userStatusData: UserStatusUpdateRequest): Promise<any> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Novedades.postStatusPresenteeismCheckResolve, { org_id: orgId, date });
        request.method = "POST";
        request.body = userStatusData;
        this.addOrganizationHeader(request, orgId);
        return this.httpClient.execute(request);
    }

    async changeUserStatusInBulk(orgId: string, bulkStatusData: BulkUserStatusUpdateRequest): Promise<any> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Novedades.postStatusPresenteeismCheckResolveBulk, { org_id: orgId });
        request.method = "POST";
        request.body = bulkStatusData
        this.addOrganizationHeader(request, orgId);
        return this.httpClient.execute(request);
    }

    async getReporteNovedades(orgId: string, yearMonth: string): Promise<any> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Novedades.getReporte, { org_id: orgId });
        request.method = "POST";
        request.body = { yearMonth };
        this.addOrganizationHeader(request, orgId);
        const response = await this.httpClient.execute(request);
        return response.result;
    }

    async getNovedades(orgId: string): Promise<Novedad[]> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Novedades.getNovedades, { org_id: orgId });
        request.method = "GET";
        this.addOrganizationHeader(request, orgId);
        const response = await this.httpClient.execute(request);
        return response.result as Novedad[];
    }

    async getAuditLogs(orgId: string): Promise<NovedadesAuditLog[]> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Novedades.getAudit, { org_id: orgId });
        request.method = "GET";
        this.addOrganizationHeader(request, orgId);
        const response = await this.httpClient.execute(request);
        return (response.result as DbNovedadesAuditLog[]).map(convertDbAuditLogToNovedadesAuditLog);
    }

    async createNovedad(orgId: string, novedadData: CreateNovedadRequest): Promise<any> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Novedades.postCreateNovedad, { org_id: orgId });
        request.method = "POST";
        request.body = novedadData;
        this.addOrganizationHeader(request, orgId);
        return this.httpClient.execute(request);
    }

    async disableNovedad(orgId: string, disableData: DisableNovedadRequest): Promise<any> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Novedades.postDisableNovedad, { org_id: orgId });
        request.method = "POST";
        request.body = disableData;
        this.addOrganizationHeader(request, orgId);
        return this.httpClient.execute(request);
    }

    async setNovedadLicenseEnabled(orgId: string, novedadData: LicenseEnabledNovedadRequest): Promise<any> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Novedades.postLicenseEnabledNovedad, { org_id: orgId });
        request.method = "POST";
        request.body = novedadData;
        this.addOrganizationHeader(request, orgId);
        return this.httpClient.execute(request);
    }

    async getConfigurationDias(orgId: string): Promise<any> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Novedades.getConfigurationDias, { org_id: orgId });
        request.method = "GET";
        this.addOrganizationHeader(request, orgId);

        const response = await this.httpClient.execute(request);
        return response.result;
    }

    async setConfigurationDias(orgId: string, workingDays: WorkingDaysRequest): Promise<any> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Novedades.postConfigurationDias, { org_id: orgId });
        request.method = "POST";
        request.body = workingDays;
        this.addOrganizationHeader(request, orgId);
        
        return this.httpClient.execute(request);
    }

    async getConfigurationFeriados(orgId: string): Promise<any> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Novedades.getConfigurationFeriados, { org_id: orgId });
        request.method = "GET";
        this.addOrganizationHeader(request, orgId);
        
        const response = await this.httpClient.execute(request);
        return response.result;
    }

    async setConfigurationFeriados(orgId: string, holidays: HolidaysRequest): Promise<any> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Novedades.postConfigurationFeriados, { org_id: orgId });
        request.method = "POST";
        request.body = holidays;
        this.addOrganizationHeader(request, orgId);
        
        return this.httpClient.execute(request);
    }
}
